import React, { useState, useEffect, useMemo } from "react";
import AddEvaluation from "../../pages/AddEvaluation";
import { useUser } from "../../context/UserContext";
import ErrorCommonEvalEmptyClass from "./ErrorCommonEvalEmptyClass";
import { User, ChevronRight, Check, AlertTriangle, Users, Target } from "lucide-react";
import { filtrerGroupesDeBesoin } from "../../utils/groupesDeBesoin";

function AddCommonEval() {
    const [niveau, setNiveau] = useState('');
    // 'classes' | 'groupes' (vide tant que l'utilisateur n'a pas choisi)
    const [cible, setCible] = useState('');
    const [goNext, setGoNext] = useState(false);
    const { etablissementData, user } = useUser();
    const [professeurReferents, setProfesseurReferents] = useState({});

    // Matière du professeur connecté (pour filtrer profs de classe + groupes)
    const matiereProf = useMemo(() => user?.matiere?.toLowerCase() || null, [user]);

    // Groupes utilisables pour la cible "groupes" : groupes de besoin seulement
    // (un demi-groupe partage ses élèves avec leur barrette : ils recevraient le
    // devoir deux fois) + niveau + matière du coordo + prof responsable défini
    // + au moins un élève. Le filtre matière côté front est défensif (le backend
    // filtre déjà).
    const groupesValides = useMemo(() => {
        if (!etablissementData?.groupes || !niveau || !matiereProf) return [];
        return filtrerGroupesDeBesoin(etablissementData.groupes).filter(g => {
            const matchNiveau = g.niveau === `${niveau}eme` || g.niveau === niveau;
            const matchMatiere = g.matiere?.toLowerCase() === matiereProf;
            const aProf = g.professeur != null;
            const aEleves = Array.isArray(g.eleves) ? g.eleves.length > 0 : true;
            return matchNiveau && matchMatiere && aProf && aEleves;
        });
    }, [etablissementData, niveau, matiereProf]);

    // Détection des conflits de profs (uniquement pour cible 'classes')
    const conflitsProfs = useMemo(() => {
        if (!etablissementData || !niveau || !matiereProf || cible !== 'classes') return {};

        const conflits = {};
        const classesDuNiveau = etablissementData.classes.filter(cls => cls.nom.startsWith(niveau));

        classesDuNiveau.forEach(classe => {
            const profsDeClasse = etablissementData.professeurs.filter(prof =>
                prof.classes.includes(classe.id) &&
                prof.matiere.toLowerCase() === matiereProf
            );
            if (profsDeClasse.length > 1) {
                conflits[classe.id] = { classe, professeurs: profsDeClasse };
            }
        });

        return conflits;
    }, [etablissementData, niveau, matiereProf, cible]);

    // Tous les conflits sont-ils résolus ? (toujours true pour cible 'groupes')
    const allConflictsResolved = useMemo(() => {
        if (Object.keys(conflitsProfs).length === 0) return true;
        return Object.keys(conflitsProfs).every(classeId => professeurReferents[classeId]);
    }, [conflitsProfs, professeurReferents]);

    // Reset du sous-état quand on change de niveau
    const handleNiveauChange = (item) => {
        setNiveau(item);
        setCible('');
        setProfesseurReferents({});
        setGoNext(false);
    };

    // Choix de la cible : pour 'groupes', on remplit automatiquement les profs référents
    // depuis groupe.professeur (1 prof unique par groupe → pas d'écran de résolution).
    const handleCibleChange = (newCible) => {
        setCible(newCible);
        setGoNext(false);
        if (newCible === 'groupes') {
            const profsRefs = Object.fromEntries(
                groupesValides.map(g => [g.id, g.professeur])
            );
            setProfesseurReferents(profsRefs);
        } else {
            setProfesseurReferents({});
        }
    };

    // Auto-passage uniquement pour cible 'classes' sans conflits — préserve l'UX
    // existante (avant l'ajout du choix de cible). Pour 'groupes', on force le clic
    // explicite sur "Valider" afin que l'utilisateur ait le temps de voir le récap.
    useEffect(() => {
        if (niveau && cible === 'classes' && Object.keys(conflitsProfs).length === 0) {
            setGoNext(true);
        }
    }, [niveau, cible, conflitsProfs]);

    const handleProfSelection = (classeId, profId) => {
        setProfesseurReferents(prev => ({ ...prev, [classeId]: profId }));
    };

    const handleGoNext = () => {
        if (allConflictsResolved) setGoNext(true);
    };

    const evaluationAllowed = useMemo(() => {
        if (!etablissementData?.classes) {
            return { allowed: false, error: "Aucune donnée d'établissement disponible." };
        }

        if (cible === 'groupes') {
            if (groupesValides.length === 0) {
                return {
                    allowed: false,
                    error: `Aucun groupe utilisable pour le niveau ${niveau}ème dans votre matière.`
                };
            }
            return { allowed: true, error: null };
        }

        // cible 'classes' (défaut)
        const classesDuNiveau = etablissementData.classes.filter(cls => cls.nom.startsWith(niveau));
        if (classesDuNiveau.length === 0) {
            return { allowed: false, error: `Aucune classe trouvée pour le niveau ${niveau}.` };
        }

        const classesVides = classesDuNiveau.filter(cls => Number(cls.nombre_eleves) === 0);
        if (classesVides.length > 0) {
            return {
                allowed: false,
                error: `Des classes du niveau ${niveau}ème n'ont aucun élève : ${classesVides.map(c => c.nom).join(", ")}.`
            };
        }

        return { allowed: true, error: null };
    }, [etablissementData, niveau, cible, groupesValides]);

    return (
        <div className="flex justify-center w-full pt-6 xl:pt-4 2xl:pt-5 3xl:pt-6">
            {(!niveau || !cible || !goNext) && (
                <div className="flex flex-col h-fit items-center gap-8 xl:gap-4 2xl:gap-6 3xl:gap-8 border border-slate-500 bg-white rounded-3xl shadow-2xl w-fit xl:text-xs 2xl:text-sm 3xl:text-lg text-sm p-10 xl:p-5 2xl:p-7 3xl:p-10">

                    {/* Titre principal */}
                    <div className="text-center">
                        <h1 className="text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-bold text-gray-800 mb-2">
                            Création d'un devoir commun
                        </h1>
                        <p className="text-gray-600 xl:text-xs 2xl:text-sm 3xl:text-base">
                            Sélectionnez le niveau concerné par l'évaluation
                        </p>
                    </div>

                    <div className="w-full">
                        {/* Sélection du niveau */}
                        <div className="grid grid-cols-4 gap-3 mb-6">
                            {["6", "5", "4", "3"].map((niv) => (
                                <button
                                    key={niv}
                                    onClick={() => handleNiveauChange(niv)}
                                    className={`
                                        py-4 px-6 xl:py-2 xl:px-4 2xl:py-3 2xl:px-5 3xl:py-4 3xl:px-6 rounded-xl font-medium transition-all
                                        ${niveau === niv
                                            ? 'bg-gradient-to-r from-sky-600 to-sky-700 text-white shadow-lg scale-105'
                                            : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-sky-400 hover:bg-sky-50'
                                        }
                                    `}
                                >
                                    {niv}ème
                                </button>
                            ))}
                        </div>

                        {/* Sélection de la cible (visible dès qu'un niveau est choisi) */}
                        {niveau && (
                            <div className="mb-6">
                                <h3 className="text-base xl:text-sm 2xl:text-sm 3xl:text-base font-semibold text-gray-800 mb-3">
                                    À qui s'adresse ce devoir commun ?
                                </h3>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        onClick={() => handleCibleChange('classes')}
                                        className={`
                                            relative py-4 px-4 xl:py-2.5 xl:px-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2
                                            ${cible === 'classes'
                                                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg scale-[1.02]'
                                                : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-blue-400 hover:bg-blue-50'
                                            }
                                        `}
                                    >
                                        <Users className="w-4 h-4" />
                                        <span>Affecté aux classes</span>
                                    </button>
                                    <button
                                        onClick={() => handleCibleChange('groupes')}
                                        disabled={groupesValides.length === 0}
                                        className={`
                                            relative py-4 px-4 xl:py-2.5 xl:px-3 rounded-xl font-medium transition-all flex flex-col items-center justify-center gap-1
                                            ${cible === 'groupes'
                                                ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg scale-[1.02]'
                                                : groupesValides.length === 0
                                                    ? 'bg-gray-100 border-2 border-gray-200 text-gray-400 cursor-not-allowed'
                                                    : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-emerald-400 hover:bg-emerald-50'
                                            }
                                        `}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Target className="w-4 h-4" />
                                            <span>Affecté aux groupes</span>
                                        </div>
                                        {groupesValides.length === 0 && (
                                            <span className="text-[11px] opacity-80">Aucun groupe disponible</span>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Récap rapide pour cible 'groupes' (avant Valider) */}
                        {niveau && cible === 'groupes' && groupesValides.length > 0 && (
                            <div className="mt-6 xl:mt-3 2xl:mt-4 3xl:mt-6 p-4 xl:p-2 2xl:p-3 3xl:p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                                <div className="flex items-start gap-3">
                                    <Check className="w-5 h-5 text-emerald-600 mt-0.5" />
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-emerald-900 xl:text-xs 2xl:text-sm 3xl:text-base">
                                            {groupesValides.length} groupe{groupesValides.length > 1 ? 's' : ''} concerné{groupesValides.length > 1 ? 's' : ''}
                                        </h3>
                                        <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-emerald-700 mt-1">
                                            Le devoir sera affecté à chaque groupe ci-dessous, et corrigé par son prof responsable.
                                        </p>
                                        <ul className="mt-2 space-y-1 text-sm xl:text-xs">
                                            {groupesValides.map(g => {
                                                const prof = etablissementData.professeurs.find(p => p.id === g.professeur);
                                                return (
                                                    <li key={g.id} className="flex items-center gap-2 text-emerald-900">
                                                        <Target className="w-3 h-3 text-emerald-600" />
                                                        <span className="font-medium">{g.nom}</span>
                                                        {prof && (
                                                            <span className="text-emerald-700">— {prof.prenom} {prof.nom}</span>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Section des conflits de professeurs (uniquement pour cible 'classes') */}
                        {cible === 'classes' && Object.keys(conflitsProfs).length > 0 && (
                            <div className="mt-6 xl:mt-3 2xl:mt-4 3xl:mt-6 p-4 xl:p-2 2xl:p-3 3xl:p-4 bg-amber-50 border border-amber-200 rounded-xl">
                                <div className="flex items-start gap-3 mb-4">
                                    <AlertTriangle className="w-5 h-5 xl:w-4 xl:h-4 3xl:w-5 3xl:h-5 text-amber-600 mt-0.5" />
                                    <div>
                                        <h3 className="font-semibold text-amber-900 xl:text-xs 2xl:text-sm 3xl:text-base">
                                            Attribution des professeurs référents
                                        </h3>
                                        <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-amber-700 mt-1">
                                            Plusieurs professeurs enseignent dans de mêmes classes.
                                            Veuillez désigner un référent pour chaque classe.
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    {Object.entries(conflitsProfs).map(([classeId, conflit]) => (
                                        <div key={classeId} className="bg-white rounded-lg p-4 xl:p-2 2xl:p-3 3xl:p-4 border border-amber-100">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <Users className="w-4 h-4 text-sky-600" />
                                                    <h4 className="font-medium">Classe {conflit.classe.nom}</h4>
                                                </div>
                                                {professeurReferents[classeId] && (
                                                    <div className="flex items-center gap-1 text-green-600">
                                                        <Check className="w-3 h-3" />
                                                        <span className="text-xs">Sélectionné</span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="space-y-2">
                                                {conflit.professeurs.map(prof => (
                                                    <label
                                                        key={prof.id}
                                                        className={`
                                                            flex items-center gap-3 p-2 rounded-lg border cursor-pointer
                                                            transition-all hover:bg-sky-50
                                                            ${professeurReferents[classeId] === prof.id
                                                                ? 'border-sky-500 bg-sky-50'
                                                                : 'border-gray-200'
                                                            }
                                                        `}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name={`prof-${classeId}`}
                                                            value={prof.id}
                                                            checked={professeurReferents[classeId] === prof.id}
                                                            onChange={() => handleProfSelection(classeId, prof.id)}
                                                            className="w-3 h-3 text-sky-600"
                                                        />
                                                        <User className="w-3 h-3 text-gray-500" />
                                                        <div className="flex-1">
                                                            <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium">
                                                                {prof.prenom} {prof.nom}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {prof.coordonateur && "Coordonnateur • "}
                                                                {prof.classes_nom.length} classes
                                                            </p>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Bouton Valider — visible dès qu'un niveau ET une cible sont choisis */}
                    {niveau && cible && (
                        <button
                            className={`
                                px-8 py-3 xl:px-5 xl:py-2 2xl:px-6 2xl:py-2.5 3xl:px-8 3xl:py-3 rounded-xl font-medium transition-all
                                flex items-center gap-2
                                ${allConflictsResolved
                                    ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:from-teal-600 hover:to-teal-700 shadow-lg cursor-pointer'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }
                            `}
                            onClick={handleGoNext}
                            disabled={!allConflictsResolved}
                        >
                            {allConflictsResolved ? 'Valider' : 'Sélectionnez tous les référents'}
                            {allConflictsResolved && <ChevronRight className="w-4 h-4" />}
                        </button>
                    )}
                </div>
            )}

            {(goNext && evaluationAllowed.allowed) && (
                <AddEvaluation
                    niveau={niveau}
                    cible={cible}
                    professeurReferents={professeurReferents}
                />
            )}

            {(goNext && !evaluationAllowed.allowed) && (
                <div>
                    <ErrorCommonEvalEmptyClass niveau={niveau} error={evaluationAllowed.error} />
                </div>
            )}
        </div>
    );
}

export default AddCommonEval;
